/**
 * Optional Install Trial Wizard (Stage A).
 *
 * Run after a trial is awarded/claimed to pre-fill:
 *   - protocol visit schedule -> study_visits rows
 *   - chosen eCRF pack -> trial_protocol_forms
 *   - visit_form_bundles linking each visit to the right forms
 *
 * Optional by design — sites can skip and use manual workflows.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CheckCircle, Loader2, AlertCircle, Wand2, FileText, CalendarClock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useInstallTrialSite, type InstallTrialResult } from './useInstallTrialSite';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trialId: string;
  trialSiteId: string;
  trialTitle?: string;
}

export function InstallTrialWizard({ open, onOpenChange, trialId, trialSiteId, trialTitle }: Props) {
  const [packCode, setPackCode] = useState<string>('none');
  const [result, setResult] = useState<InstallTrialResult | null>(null);
  const installMutation = useInstallTrialSite();

  const { data: packs, isLoading: packsLoading } = useQuery({
    queryKey: ['ecrf-template-packs-list'],
    enabled: open,
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase as any;
      const { data, error } = await client
        .from('ecrf_template_packs')
        .select('pack_code, pack_name, version, therapeutic_area, protocol_alignment')
        .eq('is_active', true)
        .order('pack_name');
      if (error) throw error;
      return data ?? [];
    },
  });

  const handleInstall = async () => {
    try {
      const res = await installMutation.mutateAsync({
        trialId,
        trialSiteId,
        packCode: packCode === 'none' ? undefined : packCode,
      });
      setResult(res);
      toast.success('Trial installed at site', {
        description: `${res.visitsCreated} visits · ${res.formsProvisioned} new forms · ${res.bundlesCreated} bundles`,
      });
    } catch (e) {
      const msg = (e as { message?: string })?.message ?? 'Install failed';
      toast.error('Install failed', { description: msg });
    }
  };

  const handleClose = () => {
    setResult(null);
    setPackCode('none');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Install Trial at Site
          </DialogTitle>
          <DialogDescription>
            {trialTitle ? <span className="font-medium">{trialTitle}</span> : 'Pre-fill this site with visits, eCRF forms, and visit/form bundles.'}
            <br />
            <span className="text-xs">Optional — you can still set things up manually in each tab.</span>
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 py-2">
            <Card>
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CalendarClock className="h-4 w-4 text-primary" />
                  Visit schedule
                </div>
                <p className="text-xs text-muted-foreground">
                  Reads <code>trial_protocols.visit_schedule</code> and creates one <code>study_visits</code> row per visit. Falls back to a 6-visit default (Screening → EOS) if none is set.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="py-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="h-4 w-4 text-primary" />
                  eCRF pack
                </div>
                <p className="text-xs text-muted-foreground">
                  Pick a template pack to provision into <code>trial_protocol_forms</code> at this site, or skip and add forms manually later.
                </p>
                {packsLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading packs…
                  </div>
                ) : (
                  <RadioGroup value={packCode} onValueChange={setPackCode} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="none" id="pack-none" />
                      <Label htmlFor="pack-none" className="text-sm font-normal cursor-pointer">
                        Skip — I'll add forms manually
                      </Label>
                    </div>
                    {(packs ?? []).map((p: { pack_code: string; pack_name: string; version: string; therapeutic_area?: string }) => (
                      <div key={p.pack_code} className="flex items-start gap-2">
                        <RadioGroupItem value={p.pack_code} id={`pack-${p.pack_code}`} className="mt-0.5" />
                        <Label htmlFor={`pack-${p.pack_code}`} className="text-sm font-normal cursor-pointer flex-1">
                          <span className="font-medium">{p.pack_name}</span>{' '}
                          <Badge variant="outline" className="text-xs ml-1">v{p.version}</Badge>
                          {p.therapeutic_area && (
                            <span className="text-xs text-muted-foreground ml-2">· {p.therapeutic_area}</span>
                          )}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Randomisation
                </div>
                <p className="text-xs text-muted-foreground">
                  We'll check if a <code>randomisation_schedules</code> row exists for this trial. If not, the Randomisation tab will guide you to request one from the sponsor.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center gap-2 font-medium">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  Install complete
                </div>
                <ul className="text-sm space-y-1 ml-7 list-disc">
                  <li>{result.visitsCreated} new visit{result.visitsCreated === 1 ? '' : 's'} created in study_visits</li>
                  <li>
                    {result.formsProvisioned} new eCRF form{result.formsProvisioned === 1 ? '' : 's'} provisioned
                    {result.pack ? ` (${result.pack.name} v${result.pack.version})` : ''}
                  </li>
                  <li>{result.bundlesCreated} visit→form bundle{result.bundlesCreated === 1 ? '' : 's'} created</li>
                </ul>
              </CardContent>
            </Card>
            {!result.randomisationReady && (
              <Card className="border-amber-500/30 bg-amber-500/5">
                <CardContent className="py-3 flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium">No randomisation schedule yet</p>
                    <p className="text-xs text-muted-foreground">
                      Ask the sponsor/CRO to create one before you randomise the first patient.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="ghost" onClick={handleClose} disabled={installMutation.isPending}>
                Cancel
              </Button>
              <Button onClick={handleInstall} disabled={installMutation.isPending}>
                {installMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Installing…</>
                ) : (
                  <>Install at site</>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
